'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { trackCopy } from '@/lib/analytics';
import { isCloudflareImageUrl, cfImage, cfImageSrcSet, SIZES } from '@/lib/cloudflare-images';

interface HtmlContentWithCopyButtonsProps {
  html: string;
  className?: string;
}

/**
 * Renders HTML content and adds copy-to-clipboard buttons to all <pre> code blocks
 * Used for blog posts and KB articles with HTML content
 */
export default function HtmlContentWithCopyButtons({
  html,
  className = 'text-gray-700 leading-relaxed',
}: HtmlContentWithCopyButtonsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  // Track event listeners for proper cleanup
  const listenersRef = useRef<Array<{ element: Element; type: string; handler: EventListener }>>([]);

  const handleCopy = useCallback(async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      trackCopy('code_block');
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedIndex(index);
        trackCopy('code_block');
        setTimeout(() => setCopiedIndex(null), 2000);
      } catch {
        // copy failed silently
      }
      document.body.removeChild(textArea);
    }
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    // Clear previous listener tracking
    listenersRef.current = [];

    // Helper to track addEventListener calls for cleanup
    const addTrackedListener = (element: Element, type: string, handler: EventListener) => {
      element.addEventListener(type, handler);
      listenersRef.current.push({ element, type, handler });
    };

    // Defer DOM mutations to next animation frame to avoid blocking INP
    const rafId = requestAnimationFrame(() => {
      if (!containerRef.current) return;

      // Find all <pre> elements that don't already have a copy button
      const preElements = containerRef.current.querySelectorAll('pre:not(.copy-button-added)');

      preElements.forEach((pre, index) => {
        // Mark as processed
        pre.classList.add('copy-button-added');

        // Wrap in relative container
        const wrapper = document.createElement('div');
        wrapper.className = 'relative group';
        pre.parentNode?.insertBefore(wrapper, pre);
        wrapper.appendChild(pre);

        // Create copy button - always visible for better discoverability
        // Pre-render both states to avoid innerHTML updates (which cause layout recalculation and high INP)
        const button = document.createElement('button');
        button.className =
          'copy-code-btn absolute top-2 right-2 px-2 py-1 text-xs font-medium rounded bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white transition-all opacity-100';
        button.setAttribute('aria-label', 'Copy to clipboard');
        button.setAttribute('title', 'Copy to clipboard');
        button.setAttribute('data-index', String(index));

        // Pre-render both "Copy" and "Copied!" states - toggle visibility via CSS classes
        button.innerHTML = `
          <span class="copy-state flex items-center gap-1">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy
          </span>
          <span class="copied-state flex items-center gap-1 hidden">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            Copied!
          </span>
        `;

        const clickHandler = () => {
          const codeText = pre.textContent || '';
          handleCopy(codeText, index);
        };
        addTrackedListener(button, 'click', clickHandler);

        wrapper.appendChild(button);
      });

      // Make inline <code> elements (not inside <pre>) click-to-copy
      const inlineCodeElements = containerRef.current.querySelectorAll('code:not(.inline-copy-added)');
      inlineCodeElements.forEach((code) => {
        // Skip if inside a <pre> element
        if (code.closest('pre')) return;
        // Do not turn code wrappers that contain links/buttons into nested controls.
        if (code.querySelector('a, button, input, select, textarea')) return;

        // Mark as processed
        code.classList.add('inline-copy-added');

        // Add interactive styles - use a special class for code inside tables
        const isInTable = code.closest('table') !== null;
        code.classList.add('cursor-pointer', 'transition-all', 'duration-200', 'inline-code-interactive');
        if (isInTable) {
          code.classList.add('table-code-interactive');
        }
        code.setAttribute('title', 'Click to copy');
        code.setAttribute('role', 'button');
        code.setAttribute('tabindex', '0');

        const originalText = code.textContent || '';

        const showCopied = () => {
          code.classList.add('code-copied');
          code.setAttribute('title', 'Copied!');
          setTimeout(() => {
            code.classList.remove('code-copied');
            code.setAttribute('title', 'Click to copy');
          }, 1500);
        };

        const copyHandler = async () => {
          try {
            await navigator.clipboard.writeText(originalText);
            showCopied();
            trackCopy('inline_code');
          } catch {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = originalText;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
              document.execCommand('copy');
              showCopied();
              trackCopy('inline_code');
            } catch {
              // copy failed silently
            }
            document.body.removeChild(textArea);
          }
        };

        addTrackedListener(code, 'click', copyHandler as EventListener);
        addTrackedListener(code, 'keydown', ((e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            copyHandler();
          }
        }) as EventListener);
      });

      // Optimize Cloudflare Images: add responsive srcset and lazy loading
      const images = containerRef.current.querySelectorAll('img:not(.cf-optimized)');
      images.forEach((img) => {
        const src = img.getAttribute('src') || '';
        if (isCloudflareImageUrl(src)) {
          img.classList.add('cf-optimized');
          img.setAttribute('src', cfImage(src, { width: 1024, quality: 85 }));
          img.setAttribute('srcset', cfImageSrcSet(src, 'content'));
          img.setAttribute('sizes', SIZES.content);
        }
        // Lazy load all images in blog content
        if (!img.hasAttribute('loading')) {
          img.setAttribute('loading', 'lazy');
        }
      });
    }); // end requestAnimationFrame

    // Cleanup function
    return () => {
      cancelAnimationFrame(rafId);

      // Remove all tracked event listeners
      for (const { element, type, handler } of listenersRef.current) {
        element.removeEventListener(type, handler);
      }
      listenersRef.current = [];

      if (containerRef.current) {
        // Remove all added buttons and wrappers on cleanup
        const wrappers = containerRef.current.querySelectorAll('.relative.group');
        wrappers.forEach((wrapper) => {
          const pre = wrapper.querySelector('pre');
          if (pre) {
            pre.classList.remove('copy-button-added');
            wrapper.parentNode?.insertBefore(pre, wrapper);
            wrapper.remove();
          }
        });
        // Reset inline code elements
        const inlineCodes = containerRef.current.querySelectorAll('code.inline-copy-added');
        inlineCodes.forEach((code) => {
          code.classList.remove('inline-copy-added', 'cursor-pointer', 'transition-all', 'duration-200', 'inline-code-interactive', 'table-code-interactive');
          code.removeAttribute('role');
          code.removeAttribute('tabindex');
          code.setAttribute('title', '');
        });
      }
    };
  }, [html, handleCopy]);

  // Update button states when copiedIndex changes
  // Uses CSS class toggling instead of innerHTML to avoid layout recalculation (fixes high INP)
  useEffect(() => {
    if (!containerRef.current) return;

    const buttons = containerRef.current.querySelectorAll('.copy-code-btn');
    buttons.forEach((button, index) => {
      const copyState = button.querySelector('.copy-state');
      const copiedState = button.querySelector('.copied-state');

      if (!copyState || !copiedState) return;

      if (copiedIndex === index) {
        // Show "Copied!" state, hide "Copy" state
        copyState.classList.add('hidden');
        copiedState.classList.remove('hidden');
      } else {
        // Show "Copy" state, hide "Copied!" state
        copyState.classList.remove('hidden');
        copiedState.classList.add('hidden');
      }
    });
  }, [copiedIndex]);

  return (
    <div
      ref={containerRef}
      className={className}
      // Cloudflare's Email Address Obfuscation rewrites @-patterns in the SSR
      // response before React hydrates, diverging from the original D1 HTML and
      // tripping React #418. Accept the edge-rewritten DOM as authoritative.
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
